import { type ReactElement, type ReactNode, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  loadStripe,
  type StripeCardElement,
  type StripeCardElementChangeEvent,
  type StripeCardElementOptions,
  type StripeElementLocale,
} from '@stripe/stripe-js';
import { CardElement, Elements } from '@stripe/react-stripe-js';
import { Checkbox } from '@repo/design-system-kit/components/checkbox/checkbox';
import { ErrorMessage } from '@repo/design-system-kit/components/form-elements/error-message/error-message';
import { TextField } from '@repo/design-system-kit/components/text-field/text-field';
import { ResponsiveCta } from '@repo/shared-kit/components/responsive-cta/responsive-cta';

import { getStripeErrorCode, type StripeError } from './stripe-error';
import { type PaymentIntentAdapter } from './types';
import { useStripeCardPayment } from './use-stripe-card-payment';
import styles from './stripe-card-form.module.css';

export interface StripeCardFormProps {
  stripePublishableKey: string;
  displayAmount: string;
  pricePlanId: string;
  paymentIntentAdapter: PaymentIntentAdapter;
  onPaymentSuccess: () => void | Promise<void>;
  /**
   * Called when payment fails. The error is one of:
   * - a Stripe `StripeError` from card validation / payment-method creation / 3DS
   *   (has `code`, `type`; no HTTP `status`).
   * - the error thrown by the `PaymentIntentAdapter` backend calls
   *   (shape is app-owned; e.g. an HTTP error with `.status`, e.g. 451).
   */
  onPaymentError: (error: unknown) => void | Promise<void>;
  /**
   * Rendered after the card field, above the terms checkbox (if any) and the
   * submit button. Intended for app-owned content such as a disclaimer or
   * reassurance/legal text.
   */
  children?: ReactNode;
  /**
   * When set, the form renders and owns a terms-and-conditions checkbox
   * between `children` and the submit button. Submit is blocked, and
   * `errorMessage` is shown, until the checkbox is checked.
   */
  termsCheckbox?: {
    label: ReactNode;
    errorMessage: string;
  };
  /**
   * Called once a valid submit begins (after card/checkbox gating passes,
   * before the Stripe payment method is created). Intended for app-specific
   * analytics.
   */
  onPaymentSubmit?: () => void;
}

export function StripeCardForm({ stripePublishableKey, ...props }: StripeCardFormProps) {
  return (
    <StripeProvider stripePublishableKey={stripePublishableKey}>
      <StripeForm {...props} />
    </StripeProvider>
  );
}

/**
 * Locale mapping from react-intl to Stripe locale codes.
 */
const STRIPE_LOCALE_MAP: { [locale: string]: StripeElementLocale | undefined } = {
  en: 'en',
  fr: 'fr',
  de: 'de',
  es: 'es',
  it: 'it',
  nl: 'nl',
  pt: 'pt',
};

interface StripeProviderProps {
  stripePublishableKey: StripeCardFormProps['stripePublishableKey'];
  children: ReactNode;
}

function StripeProvider({ stripePublishableKey, children }: StripeProviderProps): ReactElement {
  const { locale } = useIntl();
  const stripePromise = useMemo(() => loadStripe(stripePublishableKey), [stripePublishableKey]);
  const stripeLocale = STRIPE_LOCALE_MAP[locale] ?? 'auto';

  return (
    <Elements options={{ locale: stripeLocale }} stripe={stripePromise}>
      {children}
    </Elements>
  );
}

interface StripeErrorTextProps {
  error: StripeError;
}

function StripeErrorText({ error }: StripeErrorTextProps): ReactElement | null {
  switch (error.code) {
    case 'invalid_expiry_year_past':
      return (
        <FormattedMessage id="customer-registration-kit.stripe.form.errors.invalid-expiry-year-past" />
      );
    case 'invalid_number':
      return <FormattedMessage id="customer-registration-kit.stripe.form.errors.invalid-number" />;
    case 'incomplete_number':
      return (
        <FormattedMessage id="customer-registration-kit.stripe.form.errors.incomplete-number" />
      );
    case 'incomplete_expiry':
      return (
        <FormattedMessage id="customer-registration-kit.stripe.form.errors.incomplete-expiry" />
      );
    case 'incomplete_cvc':
      return <FormattedMessage id="customer-registration-kit.stripe.form.errors.incomplete-cvc" />;
    case 'invalid_cvc':
      return <FormattedMessage id="customer-registration-kit.stripe.form.errors.invalid-cvc" />;
    case 'incorrect_number':
      return (
        <FormattedMessage id="customer-registration-kit.stripe.form.errors.incorrect-number" />
      );
    case 'unknown_error':
    default:
      return null;
  }
}

/**
 * Stripe Element card styling options.
 */
const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true, // we don't need this for our form
  style: {
    base: {
      color: '#050505',
      lineHeight: '20px',
      fontFamily:
        'QontoSans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '14px',
      '::placeholder': {
        color: '#616161',
      },
    },
    invalid: {
      color: '#b81818',
      iconColor: '#b81818',
    },
  },
} satisfies StripeCardElementOptions;

type StripeFormProps = Omit<StripeCardFormProps, 'stripePublishableKey'>;

function StripeForm({
  displayAmount,
  pricePlanId,
  paymentIntentAdapter,
  onPaymentSuccess,
  onPaymentError,
  children,
  termsCheckbox,
  onPaymentSubmit,
}: StripeFormProps): ReactElement {
  const { formatMessage } = useIntl();
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [cardError, setCardError] = useState<StripeError | null>(null);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isTermsInvalid, setIsTermsInvalid] = useState(false);
  const cardElementRef = useRef<StripeCardElement | null>(null);

  const { submit, isSubmitting } = useStripeCardPayment({
    pricePlanId,
    paymentIntentAdapter,
    onPaymentSuccess,
    onPaymentError,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (termsCheckbox && !isTermsChecked) {
      setIsTermsInvalid(true);
      return;
    }

    onPaymentSubmit?.();
    void submit(cardElementRef.current);
  };

  const handleCardChange = (event: StripeCardElementChangeEvent): void => {
    setIsCardComplete(event.complete);

    const errorCode = event.error ? getStripeErrorCode(event.error) : null;
    setCardError(errorCode && errorCode !== 'unknown_error' ? { code: errorCode } : null);
  };

  const handleTermsChange = (isSelected: boolean): void => {
    setIsTermsChecked(isSelected);
    setIsTermsInvalid(false);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* NOTE: cardholderName is not used anywhere, it's purely cosmetic. */}
      <TextField
        label={formatMessage({ id: 'customer-registration-kit.stripe.form.cardholder-label' })}
        name="cardholderName"
        placeholder={formatMessage({
          id: 'customer-registration-kit.stripe.form.cardholder-placeholder',
        })}
      />

      <div className={styles.cardField}>
        <span className={styles.cardLabel}>
          <FormattedMessage id="customer-registration-kit.stripe.form.card-details-label" />
        </span>
        <CardElement
          onChange={handleCardChange}
          onReady={cardElement => {
            cardElementRef.current = cardElement;
          }}
          options={CARD_ELEMENT_OPTIONS}
        />

        <ErrorMessage alertRole className={styles.cardError} isInvalid={Boolean(cardError)}>
          {cardError ? <StripeErrorText error={cardError} /> : null}
        </ErrorMessage>
      </div>

      {children}

      {termsCheckbox ? (
        <Checkbox
          className={styles.termsCheckbox}
          errorMessage={termsCheckbox.errorMessage}
          isInvalid={isTermsInvalid}
          isSelected={isTermsChecked}
          onChange={handleTermsChange}
        >
          {termsCheckbox.label}
        </Checkbox>
      ) : null}

      <ResponsiveCta
        className={styles.submitButton}
        isDisabled={!isCardComplete}
        isPending={isSubmitting}
        type="submit"
      >
        {formatMessage(
          { id: 'customer-registration-kit.stripe.form.submit' },
          { amount: displayAmount }
        )}
      </ResponsiveCta>
    </form>
  );
}
